import { useWorkspaceById } from "@/api/workspaces";
import { withMessageInputContext } from "@/context/MessageInputContext";
import { withSessionProvider } from "@/context/SessionContext";
import {
  type UploadTarget,
  useFileRecordUpload,
} from "@/hooks/useFileRecordUpload";
import { HomeSurface } from "@/pages/workspaces/home/HomeSurface";
import { useWorkspaceUploadQueue } from "@/pages/workspaces/hooks/useWorkspaceUploadQueue";
import { UploadingCard } from "@cohesive/ui-components";
import { useState } from "react";
import toast from "react-hot-toast";
import { MobileHomeActions } from "./MobileHomeActions";
import { MobileHomeHeader } from "./MobileHomeHeader";
import { MobileScreen } from "./MobileScreen";
import { MobileUploadStatusStrip } from "./MobileUploadStatusStrip";
import { UploadTargetSheet } from "./UploadTargetSheet";

/**
 * The mobile home: the real desktop `HomeSurface` (greeting/title → composer →
 * recent-activity browser, workspace-scoped or unscoped) inside the
 * `MobileScreen` overlay, with the capture dock pinned beneath it.
 *
 * `overflow-hidden` overrides `MobileScreen`'s own page scroll — `HomeSurface`
 * owns the scroll model (only the browser list scrolls, chrome stays fixed) —
 * and `px-0` drops the screen padding since the surface column brings its own.
 *
 * Uploads follow the scope the user has declared:
 * - scoped → straight into that workspace's root via the workspace upload
 *   queue, with ghost `UploadingCard`s in the browser's leading slot (the
 *   desktop workspace-page pattern) — no target prompt;
 * - unscoped → the existing `UploadTargetSheet` (workspace or canvas
 *   drill-down), with a compact status strip above the browser while
 *   uploads are in flight.
 */
function MobileHomeSurfaceInner({ workspaceId }: { workspaceId?: string }) {
  const { data: workspace } = useWorkspaceById(workspaceId);

  const workspaceName = workspaceId
    ? workspace?.is_private
      ? "My workspace"
      : (workspace?.name ?? "Workspace")
    : undefined;

  // Scoped: the workspace queue (conflict resolver + ghost tiles + dedupe).
  const scopedQueue = useWorkspaceUploadQueue(workspaceId);
  // Unscoped: the target-sheet flow. Same hook instance feeds the status
  // strip — the queue state is per-instance, not shared.
  const unscoped = useFileRecordUpload();
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const onUpload = (files: File[]) => {
    if (workspaceId) {
      scopedQueue.enqueueFiles(files, "");
      toast.success(
        `Uploading ${files.length} file${files.length > 1 ? "s" : ""} to ${
          workspaceName ?? "workspace"
        }…`,
      );
      return;
    }
    setPendingFiles(files);
    setSheetOpen(true);
  };

  const onSelectTarget = async (target: UploadTarget) => {
    const files = pendingFiles;
    setPendingFiles(null);
    if (!files?.length) return;
    try {
      await unscoped.enqueueFiles(files, target);
      toast.success(
        `Uploading ${files.length} file${files.length > 1 ? "s" : ""}…`,
      );
    } catch {
      toast.error("Couldn't start the upload.");
    }
  };

  // Keep the slot `undefined` while idle — its mere presence suppresses the
  // browser's empty state.
  const activeUploads = unscoped.uploadedFiles.filter(
    (f) => f.status !== "processed",
  );
  const leadingSlot = workspaceId ? (
    scopedQueue.queue.length > 0 ? (
      scopedQueue.queue.map((item) => (
        <UploadingCard
          key={item.tempId}
          entry={{
            id: item.tempId,
            name: item.name,
            status: item.status,
            error: item.error,
            // Only a failed local upload is the user's to clear.
            dismissable: item.status === "error",
          }}
          onDismiss={() => scopedQueue.cancelUpload(item.tempId)}
        />
      ))
    ) : undefined
  ) : activeUploads.length > 0 ? (
    <MobileUploadStatusStrip files={activeUploads} />
  ) : undefined;

  return (
    <MobileScreen underAppHeader className="overflow-hidden px-0">
      {/* The whole surface scrolls here (greeting → composer → browser); the
          capture dock below stays pinned as a flex sibling. `overscroll-contain`
          keeps the bounce from chaining to the webview. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <HomeSurface
          pageScroll
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          workspaceIcon={workspace?.metadata?.icon}
          title={
            <MobileHomeHeader
              workspaceId={workspaceId}
              workspaceName={workspaceName}
              workspaceIcon={workspace?.metadata?.icon}
            />
          }
          browserLeadingSlot={leadingSlot}
          suppressedRecordIds={
            workspaceId ? scopedQueue.suppressedRecordIds : undefined
          }
        />
      </div>

      <MobileHomeActions workspaceId={workspaceId} onUpload={onUpload} />

      <UploadTargetSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onSelect={onSelectTarget}
        fileCount={pendingFiles?.length}
      />
    </MobileScreen>
  );
}

/**
 * Session + message-input contexts wrapped exactly like `HomePage` /
 * `WorkspaceDetailPage`. The gates key this component by workspace id — the
 * key must sit above `withSessionProvider` so workspace A's auto-created flow
 * session can't leak into workspace B (see `WorkspaceDetailPage`).
 */
export const MobileHomeSurface = withMessageInputContext(
  withSessionProvider(MobileHomeSurfaceInner),
);

export default MobileHomeSurface;
